import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import { parseGranularity } from '@/components/activity/activity-panel'
import type { ActivityGranularity } from '@/models/IActivity'

export type UserDrawerTab = 'activity' | 'audit'

/** search params owned by the organization Metrics tab (?tab=activity) */
export const ORG_ACTIVITY_PARAMS = ['granularity', 'period', 'user', 'utab']

/**
 * URL state of the Metrics tab:
 * `?tab=activity&granularity=week&period=<period_start>&user=<user_id>&utab=audit`.
 * Updates push a history entry and keep all other params.
 */
export const useActivityParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const granularity: ActivityGranularity = parseGranularity(
    searchParams.get('granularity')
  )
  const period = searchParams.get('period')
  const user = searchParams.get('user')
  const userTab: UserDrawerTab =
    searchParams.get('utab') === 'audit' ? 'audit' : 'activity'

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)

        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === '') next.delete(key)
          else next.set(key, value)
        }

        return next
      }),
    [setSearchParams]
  )

  return {
    granularity,
    period,
    user,
    userTab,
    setGranularity: (value: ActivityGranularity) =>
      // a selected period belongs to the previous bucket size
      update({ granularity: value, period: null }),
    setPeriod: (value: string | null) => update({ period: value }),
    openUser: (userId: string) => update({ user: userId, utab: null }),
    closeUser: () => update({ user: null, utab: null }),
    setUserTab: (value: UserDrawerTab) =>
      update({ utab: value === 'activity' ? null : value })
  }
}
